import React, { useState, useEffect } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import fetchDataFromTwoTables from '../../functions/fetchdatawithTwoTables';
import fetchData from '../../functions/fetchData';
import Layout from "../../layout/Layout";
import Header from "../../components/Header";
import './CategoryWiseSales.css';

function CategoryWiseSales() {
  const [categoryData, setCategoryData] = useState([]);
  const [topItemsByCategory, setTopItemsByCategory] = useState({});
  const [dailyTrendData, setDailyTrendData] = useState({});
  const [summaryData, setSummaryData] = useState({
    totalCategories: 0,
    totalSales: 0,
    totalQuantity: 0,
    totalBills: 0,
    uniqueItems: 0,
    topCategory: '',
    topCategorySales: 0,
    averageSalesPerCategory: 0,
    categoriesWithSales: 0
  });
  const [performanceAnalysis, setPerformanceAnalysis] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  const fetchCategoryWiseSalesData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Use fetchDataFromTwoTables to join bill_items with categories table
      const billItemsWithCategories = await fetchDataFromTwoTables(
        'bill_items',     // First table
        'categories',     // Second table  
        'category_id',    // Column from bill_items to join on
        'id',            // Column from categories to join on
        null,            // No setData function needed
        't1.created_at', // Order by bill_items.created_at
        {}               // No where conditions - we'll filter after
      );
      
      // Also fetch bills data to filter by date range
      const billsData = await fetchData('bills', null, 'bill_date', {});
      
      if (billItemsWithCategories && Array.isArray(billItemsWithCategories) && billsData && Array.isArray(billsData)) {
        // Filter bills by selected year and month
        const filteredBills = billsData.filter(bill => {
          const billDate = new Date(bill.bill_date);
          return billDate.getFullYear() === selectedYear && 
                 (billDate.getMonth() + 1) === selectedMonth && 
                 bill.status === 'active';
        });
        
        // Get bill IDs from filtered bills
        const billIds = filteredBills.map(bill => bill.id);
        
        // Filter bill_items to only include items from filtered bills
        const filteredBillItems = billItemsWithCategories.filter(item => 
          billIds.includes(item.bill_id)
        );
        
        // Process category data
        const categoryBreakdown = {};
        let totalSales = 0;
        let totalQuantity = 0;
        const uniqueItems = new Set();
        
        filteredBillItems.forEach(item => {
          // Use category name from categories table (t2.name) or fallback to bill_items category_name
          const categoryName = item.name || item.category_name || 'Other';
          const itemName = item.item_name || 'Unknown Item';
          const totalPrice = parseFloat(item.total_price) || 0;
          const quantity = parseInt(item.quantity) || 0;
          
          // Track unique items
          uniqueItems.add(itemName);
          
          // Initialize category if not exists
          if (!categoryBreakdown[categoryName]) {
            categoryBreakdown[categoryName] = {
              category_name: categoryName,
              total_sale: 0,
              total_quantity: 0,
              item_count: 0,
              items: new Set()
            };
          }
          
          // Add to category totals
          categoryBreakdown[categoryName].total_sale += totalPrice;
          categoryBreakdown[categoryName].total_quantity += quantity;
          categoryBreakdown[categoryName].items.add(itemName);
          
          // Add to overall totals
          totalSales += totalPrice;
          totalQuantity += quantity;
        });
        
        // Convert to array and add item counts
        const categoryArray = Object.values(categoryBreakdown).map(category => ({
          ...category,
          item_count: category.items.size,
          items: undefined // Remove the Set object
        }));
        
        // Sort by total sales descending
        categoryArray.sort((a, b) => b.total_sale - a.total_sale);
        
        setCategoryData(categoryArray);
        
        // Calculate summary data
        const topCategoryData = categoryArray.length > 0 ? categoryArray[0] : {};
        const totalCategories = categoryArray.length;
        const categoriesWithSales = categoryArray.filter(cat => cat.total_sale > 0).length;
        
        setSummaryData({
          totalCategories,
          totalSales,
          totalQuantity,
          totalBills: filteredBills.length,
          uniqueItems: uniqueItems.size,
          topCategory: topCategoryData.category_name || '',
          topCategorySales: topCategoryData.total_sale || 0,
          averageSalesPerCategory: totalCategories > 0 ? totalSales / totalCategories : 0,
          categoriesWithSales
        });
        
        // Calculate performance analysis
        const performanceAnalysisData = {
          best_performing_category: {
            category: topCategoryData.category_name || '',
            sale_amount: topCategoryData.total_sale || 0,
            percentage: totalSales > 0 ? ((topCategoryData.total_sale || 0) / totalSales * 100).toFixed(1) : 0
          },
          worst_performing_category: categoryArray.length > 0 ? {
            category: categoryArray[categoryArray.length - 1].category_name || '',
            sale_amount: categoryArray[categoryArray.length - 1].total_sale || 0,
            percentage: totalSales > 0 ? ((categoryArray[categoryArray.length - 1].total_sale || 0) / totalSales * 100).toFixed(1) : 0
          } : {},
          categories_with_no_sales: totalCategories - categoriesWithSales,
          market_concentration: categoriesWithSales > 0 ? 
            `Top category represents ${totalSales > 0 ? ((topCategoryData.total_sale || 0) / totalSales * 100).toFixed(1) : 0}% of total sales` : 
            'No sales data available'
        };
        
        setPerformanceAnalysis(performanceAnalysisData);
        
        // Reset other data that's not needed for this implementation
        setTopItemsByCategory({});
        setDailyTrendData({});
        
      } else {
        // Handle case when no data is returned
        setCategoryData([]);
        setTopItemsByCategory({});
        setDailyTrendData({});
        setPerformanceAnalysis({});
        setSummaryData({
          totalCategories: 0,
          totalSales: 0,
          totalQuantity: 0,
          totalBills: 0,
          uniqueItems: 0,
          topCategory: '',
          topCategorySales: 0,
          averageSalesPerCategory: 0,
          categoriesWithSales: 0
        });
      }
    } catch (error) {
      console.error('Error fetching category wise sales data:', error);
      setError(`Failed to load category wise sales data: ${error.message}`);
      
      // Set empty states on error
      setCategoryData([]);
      setTopItemsByCategory({});
      setDailyTrendData({});
      setPerformanceAnalysis({});
      setSummaryData({
        totalCategories: 0,
        totalSales: 0,
        totalQuantity: 0,
        totalBills: 0,
        uniqueItems: 0,
        topCategory: '',
        topCategorySales: 0,
        averageSalesPerCategory: 0,
        categoriesWithSales: 0
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCategoryWiseSalesData();
  }, [selectedYear, selectedMonth]);

  const handleYearChange = (e) => {
    const newYear = parseInt(e.target.value);
    setSelectedYear(newYear);
  };

  const handleMonthChange = (e) => {
    const newMonth = parseInt(e.target.value);
    setSelectedMonth(newMonth);
  };

  // Generate month options
  const generateMonthOptions = () => {
    const months = [
      { value: 1, name: 'January' },
      { value: 2, name: 'February' },
      { value: 3, name: 'March' },
      { value: 4, name: 'April' },
      { value: 5, name: 'May' },
      { value: 6, name: 'June' },
      { value: 7, name: 'July' },
      { value: 8, name: 'August' },
      { value: 9, name: 'September' },
      { value: 10, name: 'October' },
      { value: 11, name: 'November' },
      { value: 12, name: 'December' }
    ];
    
    return months.map(month => (
      <option key={month.value} value={month.value}>
        {month.name}
      </option>
    ));
  };

  // Generate year options
  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear - 5; year <= currentYear + 1; year++) {
      years.push(year);
    }
    return years.map(year => (
      <option key={year} value={year}>
        {year}
      </option>
    ));
  };
  

  const COLORS = ['#d90429', '#3498db', '#27ae60', '#f39c12', '#9b59b6', '#e74c3c', '#1abc9c', '#34495e', '#e67e22', '#95a5a6', '#f1c40f', '#8e44ad', '#16a085', '#c0392b', '#2980b9', '#27ae60'];

  if (isLoading) {
    return (
      <Layout>
        <Header title="Category Wise Sales Report" />
        <div className="container-fluid">
          <div className="row justify-content-center mt-5">
            <div className="col-md-6 text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <p className="mt-3">Loading category wise sales report...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Header title="Category Wise Sales Report" />
      <div className="category-sales-container">
        
        {/* Controls */}
        <div className="row mb-4">
          <div className="col-md-3">
            <div className="form-group">
              <label htmlFor="yearSelect">Select Year:</label>
              <select
                id="yearSelect"
                className="form-control"
                value={selectedYear}
                onChange={handleYearChange}
              >
                {generateYearOptions()}
              </select>
            </div>
          </div>
          <div className="col-md-3">
            <div className="form-group">
              <label htmlFor="monthSelect">Select Month:</label>
              <select
                id="monthSelect"
                className="form-control"
                value={selectedMonth}
                onChange={handleMonthChange}
              >
                {generateMonthOptions()}
              </select>
            </div>
          </div>
          <div className="col-md-6 text-end">
            <button 
              className="btn btn-outline-primary"
              onClick={fetchCategoryWiseSalesData}
              disabled={isLoading}
            >
              <i className="fas fa-sync-alt me-2"></i>
              Refresh Data
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger mb-4">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </div>
        )}

        {/* Summary Cards */}
        <div className="row mb-4">
          <div className="col-lg-2 col-md-4 mb-3">
            <div className="summary-card total-categories">
              <div className="card-icon">
                <i className="fas fa-layer-group"></i>
              </div>
              <div className="card-info">
                <h3>{summaryData.totalCategories}</h3>
                <p>Total Categories</p>
              </div>
            </div>
          </div>
          
          <div className="col-lg-2 col-md-4 mb-3">
            <div className="summary-card total-sales">
              <div className="card-icon">
                <i className="fas fa-chart-line"></i>
              </div>
              <div className="card-info">
                <h3>฿{summaryData.totalSales.toLocaleString()}</h3>
                <p>Total Sales</p>
              </div>
            </div>
          </div>
          
          <div className="col-lg-2 col-md-4 mb-3">
            <div className="summary-card total-quantity">
              <div className="card-icon">
                <i className="fas fa-cubes"></i>
              </div>
              <div className="card-info">
                <h3>{summaryData.totalQuantity.toLocaleString()}</h3>
                <p>Total Quantity</p>
              </div>
            </div>
          </div>

          <div className="col-lg-2 col-md-4 mb-3">
            <div className="summary-card total-bills">
              <div className="card-icon">
                <i className="fas fa-receipt"></i>
              </div>
              <div className="card-info">
                <h3>{summaryData.totalBills.toLocaleString()}</h3>
                <p>Total Bills</p>
              </div>
            </div>
          </div>

          <div className="col-lg-2 col-md-4 mb-3">
            <div className="summary-card unique-items">
              <div className="card-icon">
                <i className="fas fa-boxes"></i>
              </div>
              <div className="card-info">
                <h3>{summaryData.uniqueItems}</h3>
                <p>Unique Items</p>
              </div>
            </div>
          </div>
          
          <div className="col-lg-2 col-md-4 mb-3">
            <div className="summary-card top-category">
              <div className="card-icon">
                <i className="fas fa-trophy"></i>
              </div>
              <div className="card-info">
                <h3>{summaryData.topCategory}</h3>
                <p>Top Category (฿{summaryData.topCategorySales.toLocaleString()})</p>
              </div>
            </div>
          </div>
        </div>

        {/* Performance Analysis */}
        {performanceAnalysis && Object.keys(performanceAnalysis).length > 0 && (
          <div className="row mb-4" key={`performance-${selectedYear}-${selectedMonth}`}>
            <div className="col-12">
              <div className="chart-panel">
                <div className="panel-header">
                  <h5>Performance Analysis</h5>
                </div>
                <div className="panel-content">
                  <div className="row">
                    <div className="col-md-4">
                      <div className="performance-metric">
                        <h6>Best Performing Category</h6>
                        <p><strong>{performanceAnalysis.best_performing_category?.category}</strong></p>
                        <p>Sale: ฿{parseFloat(performanceAnalysis.best_performing_category?.sale_amount || 0).toLocaleString()}</p>
                        <p>Market Share: {performanceAnalysis.best_performing_category?.percentage}%</p>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="performance-metric">
                        <h6>Worst Performing Category</h6>
                        <p><strong>{performanceAnalysis.worst_performing_category?.category}</strong></p>
                        <p>Sale: ฿{parseFloat(performanceAnalysis.worst_performing_category?.sale_amount || 0).toLocaleString()}</p>
                        <p>Market Share: {performanceAnalysis.worst_performing_category?.percentage}%</p>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="performance-metric">
                        <h6>Market Analysis</h6>
                        <p>Categories with Sales: {summaryData.categoriesWithSales}</p>
                        <p>No Sales: {performanceAnalysis.categories_with_no_sales || 0}</p>
                        <p>{performanceAnalysis.market_concentration}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Charts Row */}
        <div className="row" key={`charts-${selectedYear}-${selectedMonth}`}>
          {/* Bar Chart */}
          <div className="col-lg-8 col-md-12 mb-4">
            <div className="chart-panel">
              <div className="panel-header">
                <h5>Category Sales Comparison</h5>
              </div>
              <div className="panel-content">
                {categoryData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={400} key={`bar-chart-${selectedYear}-${selectedMonth}`}>
                    <BarChart data={categoryData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis 
                        dataKey="category_name" 
                        stroke="#666" 
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        fontSize={12}
                      />
                      <YAxis stroke="#666" />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: '#fff',
                          border: '1px solid #ddd',
                          borderRadius: '8px'
                        }}
                        formatter={(value, name) => [
                          name === 'total_sale' ? `฿${parseFloat(value).toLocaleString()}` : value,
                          name === 'total_sale' ? 'Sales' : 'Quantity'
                        ]}
                      />
                      <Bar dataKey="total_sale" name="total_sale">
                        {categoryData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="no-data">
                    <i className="fas fa-chart-bar"></i>
                    <p>No category sales data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Pie Chart */}
          <div className="col-lg-4 col-md-12 mb-4">
            <div className="chart-panel">
              <div className="panel-header">
                <h5>Category Distribution</h5>
              </div>
              <div className="panel-content">
                {categoryData.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={300} key={`pie-chart-${selectedYear}-${selectedMonth}`}>
                      <PieChart>
                        <Pie
                          data={categoryData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ category_name, percent }) => `${category_name} ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="total_sale"
                        >
                          {categoryData.map((entry, index) => (
                            <Cell key={`cell-${index}-${selectedYear}-${selectedMonth}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value) => [`฿${parseFloat(value).toLocaleString()}`, 'Sales']}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    
                    {/* Legend */}
                    <div className="chart-legend" key={`legend-${selectedYear}-${selectedMonth}`}>
                      {categoryData.map((category, index) => (
                        <div key={`legend-${index}-${selectedYear}-${selectedMonth}`} className="legend-item">
                          <div 
                            className="legend-color" 
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          ></div>
                          <span className="legend-text">{category.category_name}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="no-data">
                    <i className="fas fa-chart-pie"></i>
                    <p>No distribution data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Trend Chart */}
        {Object.keys(dailyTrendData).length > 0 && (
          <div className="row">
            <div className="col-12 mb-4">
              <div className="chart-panel">
                <div className="panel-header">
                  <h5>Category Sales Trend</h5>
                </div>
                <div className="panel-content">
                  <div className="text-center text-muted">
                    <i className="fas fa-chart-line"></i>
                    <p>Daily trend visualization will be available when trend data structure is finalized</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Data Table */}
        <div className="row" key={`table-${selectedYear}-${selectedMonth}`}>
          <div className="col-12">
            <div className="table-panel">
              <div className="panel-header">
                <h5>Detailed Category Report</h5>
              </div>
              <div className="panel-content">
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Sales Amount</th>
                        <th>Items Sold</th>
                        <th>Avg per Item</th>
                        <th>% of Total</th>
                        <th>Rank</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categoryData.length > 0 ? (
                        categoryData
                          .sort((a, b) => parseFloat(b.total_sale || 0) - parseFloat(a.total_sale || 0))
                          .map((category, index) => {
                            const avgPerItem = category.total_quantity > 0 ? parseFloat(category.total_sale) / category.total_quantity : 0;
                            const percentOfTotal = summaryData.totalSales > 0 ? (parseFloat(category.total_sale) / summaryData.totalSales * 100) : 0;
                            
                            return (
                              <tr key={`table-row-${index}-${selectedYear}-${selectedMonth}`}>
                                <td className="font-weight-bold">
                                  <div className="d-flex align-items-center">
                                    <div 
                                      className="category-color-indicator"
                                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                                    ></div>
                                    {category.category_name}
                                  </div>
                                </td>
                                <td className="text-success">฿{parseFloat(category.total_sale || 0).toLocaleString()}</td>
                                <td>{category.total_quantity || 0}</td>
                                <td>฿{avgPerItem.toFixed(2)}</td>
                                <td>{percentOfTotal.toFixed(1)}%</td>
                                <td>
                                  <span className={`rank-badge rank-${index + 1}`}>
                                    #{index + 1}
                                  </span>
                                </td>
                              </tr>
                            );
                          })
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center text-muted">
                            No category data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </Layout>
  );

};
export default CategoryWiseSales;
